'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Image01Icon } from '@terra/ui-icons';
import type { WebPageFeatureReturnDto } from '@terra/shared-contracts-web-pages';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { Spinner } from '../../../components/spinner';
import {
  BodyDefault,
  BodyStrong,
  HeadingItem,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';
import { Scroll } from '../../Scroll';
import { ImageWithSkeleton } from '../ImageWithSkeleton';
import { useFeatureOverrideRun } from '../hooks/useFeatureOverrideRun';
import { FeatureAuditTrailSection } from './FeatureAuditTrailSection';
import { FeatureOverrideRunDialog } from './FeatureOverrideRunDialog';
import { FeatureTypeBadge } from './FeatureTypeBadge';
import {
  FeatureRelatedRequestsSection,
  type FeatureRelatedRequestActions,
} from './FeatureRelatedRequestsSection';
import {
  canUnblockFeature,
  getFeatureCoverage,
  isFeatureOverrideRunning,
  parseReproduceSteps,
} from './feature-analysis.utils';

type FeatureDetailPaneProps = {
  feature: WebPageFeatureReturnDto | null;
  webpageId: string;
  webpageScreenshotUrl?: string | null;
  relatedRequestActions?: FeatureRelatedRequestActions;
  isOverrideStarting?: boolean;
  isOverrideDialogRequested?: boolean;
  onFeatureOverrideStarted?: (featureId: string) => void;
  onOverrideDialogRequestHandled?: () => void;
};

type FeatureAuditEvent = WebPageFeatureReturnDto['auditTrail'][number];
type FeatureAuditEventKind = FeatureAuditEvent['kind'];

const resolveScreenshotUrl = ({
  featureScreenshotUrl,
  webpageScreenshotUrl,
}: {
  featureScreenshotUrl?: string | null;
  webpageScreenshotUrl?: string | null;
}): string | null =>
  featureScreenshotUrl?.trim() || webpageScreenshotUrl?.trim() || null;

const FAILED_AUDIT_EVENT_KINDS: FeatureAuditEventKind[] = [
  'reproduce_failed',
  'mapping_failed',
];

const hasFailedAuditEvent = (auditTrail: FeatureAuditEvent[]): boolean =>
  auditTrail.some((event) => FAILED_AUDIT_EVENT_KINDS.includes(event.kind));

const FeatureScreenshot = ({
  description,
  featureScreenshotUrl,
  webpageScreenshotUrl,
  unavailableLabel,
}: {
  description: string;
  featureScreenshotUrl?: string | null;
  webpageScreenshotUrl?: string | null;
  unavailableLabel: string;
}) => {
  const screenshotUrl = resolveScreenshotUrl({
    featureScreenshotUrl,
    webpageScreenshotUrl,
  });

  return (
    <Box
      w="full"
      maxW="full"
      minW={0}
      aspectRatio="16/10"
      maxH="520px"
      bg="bg.subtle"
      overflow="hidden"
      position="relative"
      borderRadius="md"
      borderWidth={1}
      borderColor="border.subtle"
    >
      {screenshotUrl ? (
        <ImageWithSkeleton
          src={screenshotUrl}
          alt={description}
          errorMessage={unavailableLabel}
          objectFit="contain"
        />
      ) : (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack gap={2} color="fg.muted">
            <Image01Icon size={22} />
            <MetaDefault>{unavailableLabel}</MetaDefault>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export const FeatureDetailPane = ({
  feature,
  webpageId,
  webpageScreenshotUrl,
  relatedRequestActions,
  isOverrideStarting,
  isOverrideDialogRequested,
  onFeatureOverrideStarted,
  onOverrideDialogRequestHandled,
}: FeatureDetailPaneProps) => {
  const t = useTranslations('designSystem.applicationAssets.drawer.features');
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const [providedExecutionContext, setProvidedExecutionContext] = useState('');
  const { runFeatureOverride, isRunningFeatureOverride } =
    useFeatureOverrideRun();
  const reproduceSteps = useMemo(
    () => parseReproduceSteps(feature?.reproduce ?? null),
    [feature?.reproduce],
  );

  useEffect(() => {
    if (!isOverrideDialogRequested) {
      return;
    }

    setIsOverrideDialogOpen(true);
    onOverrideDialogRequestHandled?.();
  }, [isOverrideDialogRequested, onOverrideDialogRequestHandled]);

  if (!feature) {
    return (
      <Box
        h="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={6}
      >
        <MetaDefault color="fg.muted">{t('selectFeature')}</MetaDefault>
      </Box>
    );
  }

  const coverage = getFeatureCoverage(feature);
  const isOverrideInProgress =
    Boolean(isOverrideStarting) ||
    isRunningFeatureOverride ||
    isFeatureOverrideRunning(feature);
  const canRunOverride = canUnblockFeature({ feature, isOverrideInProgress });
  const isBlockedBadgeVisible = coverage === 'blocked' && !isOverrideInProgress;
  const shouldShowAuditTrail =
    feature.auditTrail.length > 0 &&
    (feature.executionPolicy === 'block' ||
      hasFailedAuditEvent(feature.auditTrail));

  const handleOverrideDialogOpenChange = (open: boolean) => {
    setIsOverrideDialogOpen(open);
    if (!open) {
      setProvidedExecutionContext('');
    }
  };

  const handleOverrideRun = () => {
    runFeatureOverride(
      {
        webpageId,
        featureId: feature.id,
        providedExecutionContext,
      },
      {
        onSuccess: () => {
          onFeatureOverrideStarted?.(feature.id);
          handleOverrideDialogOpenChange(false);
        },
      },
    );
  };

  return (
    <>
      <Scroll orientation="vertical" h="full" w="full">
        <VStack gap={4} p={4} alignItems="stretch" w="full" minW={0}>
          <HStack
            gap={3}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <HStack gap={2} alignItems="center" flexWrap="wrap" minW={0}>
              <HeadingItem color="fg.default">
                {feature.description}
              </HeadingItem>
              <FeatureTypeBadge featureType={feature.featureType} />
              {isOverrideInProgress && (
                <Spinner size="sm" aria-label={t('lastRunRunning')} />
              )}
              {isBlockedBadgeVisible && (
                <Badge variant="outline" size="sm" colorPalette="yellow">
                  {t('blocked')}
                </Badge>
              )}
            </HStack>
            {canRunOverride && (
              <Button
                size="xs"
                h="7"
                px="2.5"
                flexShrink={0}
                onClick={() => setIsOverrideDialogOpen(true)}
              >
                <LabelDefault as="span">{t('overrideRun.button')}</LabelDefault>
              </Button>
            )}
          </HStack>

          <FeatureScreenshot
            description={feature.description}
            featureScreenshotUrl={feature.featureScreenshotUrl}
            webpageScreenshotUrl={webpageScreenshotUrl}
            unavailableLabel={t('imageUnavailable')}
          />

          {reproduceSteps.length > 0 && (
            <Box>
              <BodyStrong color="fg.default" mb={2}>
                {t('reproduce')}
              </BodyStrong>
              <VStack gap={1.5} alignItems="stretch">
                {reproduceSteps.map((step, index) => (
                  <HStack
                    key={`${index}-${step}`}
                    gap={2}
                    alignItems="flex-start"
                  >
                    <BodyDefault color="fg.muted" flexShrink={0}>
                      {index + 1}.
                    </BodyDefault>
                    <BodyDefault color="fg.muted">{step}</BodyDefault>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <FeatureRelatedRequestsSection
            feature={feature}
            webpageId={webpageId}
            actions={relatedRequestActions}
          />

          {shouldShowAuditTrail && (
            <FeatureAuditTrailSection auditTrail={feature.auditTrail} />
          )}
        </VStack>
      </Scroll>
      <FeatureOverrideRunDialog
        open={isOverrideDialogOpen}
        featureDescription={feature.description}
        reproduceSteps={reproduceSteps}
        context={providedExecutionContext}
        isSubmitting={isRunningFeatureOverride}
        onContextChange={setProvidedExecutionContext}
        onOpenChange={handleOverrideDialogOpenChange}
        onSubmit={handleOverrideRun}
      />
    </>
  );
};
